import PushSettings from '../../components/Push/PushSettings';
import React, {
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  useNavigate,
  useLocation,
} from 'react-router-dom';

import AppLayout from '../../components/Layout/AppLayout';
import FamilyBillingCard from '../../components/Family/FamilyBillingCard';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import AppDownloadCard from '../../components/Downloads/AppDownloadCard';
import InfoTooltip from '../../components/UI/InfoTooltip';
import '../../styles/brand-typography.css';
import './ParentSettings.css';
import Input from '../../components/UI/Input';

import {
  db,
  supabase,
} from '../../services/supabase';

import {
  useAuth,
} from '../../contexts/AuthContext';

import {
  useFamilyStatus,
} from '../../contexts/FamilyStatusContext';

import {
  PARENT_THEMES,
  useParentTheme,
} from '../../contexts/ParentThemeContext';
import {
  getFamilyTimeZoneLabel,
  getFamilyTimeZoneOptions,
} from '../../utils/timeZones';


/*
 * =========================================================
 * PARENT SETTINGS
 * =========================================================
 *
 * This page contains preferences for the currently signed-in
 * adult. Shared family membership and kid-management tools live
 * separately under Manage Family.
 * =========================================================
 */

export default function ParentSettings() {
  const location = useLocation();
  const appsSection = useRef(null);
  useEffect(() => {
    if (!location.state?.focusApps && location.hash !== '#mypiggy-apps') return;
    const section = appsSection.current;
    if (!section) return;
    let active = true;
    let frame;
    const alignApps = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(() => {
        if (active) section.scrollIntoView({ block: 'start', behavior: 'auto' });
      });
    };
    const observer = new ResizeObserver(alignApps);
    const stopFollowing = () => { active = false; observer.disconnect(); window.cancelAnimationFrame(frame); };
    section.focus({ preventScroll: true });
    alignApps();
    observer.observe(section.parentElement);
    // Stop automatic positioning as soon as the parent starts interacting.
    const events = ['wheel', 'touchstart', 'pointerdown', 'keydown'];
    events.forEach((event) => window.addEventListener(event, stopFollowing, { passive: true }));
    const timer = window.setTimeout(stopFollowing, 5000);
    return () => {
      stopFollowing();
      window.clearTimeout(timer);
      events.forEach((event) => window.removeEventListener(event, stopFollowing));
    };
  }, [location.key, location.hash, location.state?.focusApps]);


  const navigate =
    useNavigate();


  const {
    parent,
    user,
    signOut,
    refreshParent,
  } = useAuth();


  const {
    isFamilyInactive,
    canWrite: familyAccessCanWrite,
  } = useFamilyStatus();

  const isWriteLocked =
    isFamilyInactive ||
    familyAccessCanWrite === false;


  const {
    theme,
    savingTheme,
    themeError,
    updateTheme,
  } = useParentTheme();


  const [
    success,
    setSuccess,
  ] = useState('');

  const [
    themeDraft,
    setThemeDraft,
  ] = useState(theme);

  useEffect(() => {
    setThemeDraft(theme);
  }, [theme]);


  const [
    error,
    setError,
  ] = useState('');


  const [
    isEditingName,
    setIsEditingName,
  ] = useState(false);


  const [
    name,
    setName,
  ] = useState('');


  const [
    savingName,
    setSavingName,
  ] = useState(false);

  const [familyTimeZone, setFamilyTimeZone] =
    useState('America/New_York');
  const [familyTimeZoneDraft, setFamilyTimeZoneDraft] =
    useState('America/New_York');
  const [loadingFamilyTimeZone, setLoadingFamilyTimeZone] =
    useState(true);
  const [savingFamilyTimeZone, setSavingFamilyTimeZone] =
    useState(false);


  const [digest, setDigest] = useState({ weekly: false, monthly: false });
  const [digestDraft, setDigestDraft] = useState({ weekly: false, monthly: false });
  const [digestLoading, setDigestLoading] = useState(true);
  const [digestSaving, setDigestSaving] = useState(false);
  const [digestLoadFailed, setDigestLoadFailed] = useState(false);

  useEffect(() => {
    let mounted = true;
    async function loadDigests() {
      if (!parent?.id) return;
      setDigestLoading(true);
      const { data, error: loadError } = await supabase
        .from('parent_digest_preferences')
        .select('weekly_enabled, monthly_enabled')
        .eq('parent_id', parent.id).maybeSingle();
      if (!mounted) return;
      setDigestLoadFailed(Boolean(loadError));
      if (loadError) setError('Unable to load email preferences. Refresh this page to try again.');
      else {
        const next = { weekly: !!data?.weekly_enabled, monthly: !!data?.monthly_enabled };
        setDigest(next);
        setDigestDraft(next);
      }
      setDigestLoading(false);
    }
    loadDigests();
    return () => { mounted = false; };
  }, [parent?.id]);

  const saveDigests = async () => {
    if (digestSaving || digestLoading || digestLoadFailed) return;
    const next = { ...digestDraft };
    setDigestSaving(true);
    setError(''); setSuccess('');
    try {
      const { error: saveError } = await supabase.rpc('set_my_digest_preferences', {
        p_weekly: next.weekly, p_monthly: next.monthly,
      });
      if (saveError) throw saveError;
      setDigest(next);
      setSuccess('Email digest preferences saved.');
    } catch {
      setError('Unable to save email preferences. Please try again.');
    } finally { setDigestSaving(false); }
  };

  const isOwner =
    parent?.role === 'owner';

  useEffect(() => {
    let mounted = true;

    const loadFamilyTimeZone = async () => {
      if (!parent?.family_id) {
        if (mounted) setLoadingFamilyTimeZone(false);
        return;
      }

      try {
        setLoadingFamilyTimeZone(true);
        const family = await db.getFamily(parent.family_id);

        if (mounted) {
          const savedTimeZone =
            family?.time_zone || 'America/New_York';
          setFamilyTimeZone(savedTimeZone);
          setFamilyTimeZoneDraft(savedTimeZone);
        }
      } catch (loadError) {
        console.error(
          'Unable to load family time zone:',
          loadError
        );

        if (mounted) {
          setError(
            loadError?.message ||
              'Unable to load the family time zone.'
          );
        }
      } finally {
        if (mounted) setLoadingFamilyTimeZone(false);
      }
    };

    loadFamilyTimeZone();

    return () => {
      mounted = false;
    };
  }, [parent?.family_id]);


  useEffect(() => {
    if (!isEditingName) {
      setName(
        parent?.name || ''
      );
    }
  }, [
    isEditingName,
    parent?.name,
  ]);


  const handleCancelNameEdit =
    () => {
      setName(
        parent?.name || ''
      );
      setIsEditingName(false);
      setError('');
    };


  const handleSaveName =
    async (event) => {
      event.preventDefault();

      if (
        savingName ||
        isWriteLocked
      ) {
        return;
      }

      const nextName =
        name.trim();

      setError('');
      setSuccess('');

      if (!nextName) {
        setError(
          'Name is required.'
        );
        return;
      }

      if (
        nextName.length > 80
      ) {
        setError(
          'Name must be 80 characters or fewer.'
        );
        return;
      }

      setSavingName(true);

      try {
        await db.updateMyParentName(
          nextName
        );

        await refreshParent();

        setName(nextName);
        setIsEditingName(false);
        setSuccess(
          'Your name has been updated.'
        );
      } catch (nameSaveError) {
        console.error(
          'Parent name update error:',
          nameSaveError
        );

        setError(
          nameSaveError?.message ||
            'Unable to update your name.'
        );
      } finally {
        setSavingName(false);
      }
    };


  const handleThemeSave =
    async () => {

      if (
        isWriteLocked ||
        themeDraft === theme ||
        savingTheme
      ) {
        return;
      }


      setError('');
      setSuccess('');


      try {

        await updateTheme(
          themeDraft
        );

        await refreshParent();


        setSuccess(
          `${
            themeDraft ===
            PARENT_THEMES.DARK
              ? 'Dark'
              : 'Light'
          } theme saved to your parent account.`
        );

      } catch (themeSaveError) {

        setError(
          themeSaveError?.message ||
            'Unable to save your theme preference.'
        );
      }
    };

  const handleFamilyTimeZoneSave = async () => {
    if (
      !isOwner ||
      isWriteLocked ||
      savingFamilyTimeZone ||
      familyTimeZoneDraft === familyTimeZone
    ) {
      return;
    }

    try {
      setError('');
      setSuccess('');
      setSavingFamilyTimeZone(true);

      const savedTimeZone =
        await db.updateMyFamilyTimeZone(
          familyTimeZoneDraft
        );

      setFamilyTimeZone(
        savedTimeZone || familyTimeZoneDraft
      );
      setFamilyTimeZoneDraft(
        savedTimeZone || familyTimeZoneDraft
      );
      await refreshParent();

      setSuccess(
        `Family time zone saved as ${getFamilyTimeZoneLabel(
          savedTimeZone || familyTimeZoneDraft
        )}.`
      );
    } catch (saveError) {
      console.error(
        'Unable to save family time zone:',
        saveError
      );
      setError(
        saveError?.message ||
          'Unable to save the family time zone.'
      );
    } finally {
      setSavingFamilyTimeZone(false);
    }
  };


  return (
    <AppLayout
      title="Parent Settings"
      className="mypiggy-brand parent-settings"
      showBack
      onBack={() =>
        navigate(
          '/parent'
        )
      }
    >

      <div className="space-y-4">

        {error && (
          <Card className="p-4 border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-950/35">
            <div
              role="alert"
              className="text-red-700 dark:text-red-300 font-medium"
            >
              {error}
            </div>
          </Card>
        )}


        {success && (
          <Card className="p-4 border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/35">
            <div
              role="status"
              className="text-green-700 dark:text-green-300 font-medium"
            >
              {success}
            </div>
          </Card>
        )}


        <Card className="p-4 sm:p-5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <h2 className="mb-3 text-lg font-extrabold">My Profile</h2>
              {isEditingName ? (
                      <form
                        onSubmit={
                          handleSaveName
                        }
                        className="space-y-3 max-w-md"
                      >
                        <Input
                          label=""
                          aria-label="Name"
                          value={name}
                          onChange={(event) =>
                            setName(
                              event.target.value
                            )
                          }
                          maxLength={80}
                          autoComplete="name"
                          disabled={
                            savingName ||
                            isWriteLocked
                          }
                          autoFocus
                          required
                        />

                        <div className="flex flex-wrap gap-2">
                          <Button
                            type="submit"
                            variant="primary"
                            disabled={
                              savingName ||
                              isWriteLocked
                            }
                          >
                            {savingName
                              ? 'Saving...'
                              : 'Save Name'}
                          </Button>

                          <Button
                            type="button"
                            variant="secondary"
                            onClick={
                              handleCancelNameEdit
                            }
                            disabled={savingName}
                          >
                            Cancel
                          </Button>
                        </div>
                      </form>
              ) : (
                <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
                  <p className="text-lg font-extrabold">{parent?.name || 'Parent'}</p>
                  <button type="button" onClick={() => { setError(''); setSuccess(''); setIsEditingName(true); }} disabled={isWriteLocked}
                    className="rounded px-1 py-2 text-sm font-bold text-blue-700 underline underline-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:text-blue-300">Edit Name</button>
                  <span className="rounded-full bg-teal-50 px-2.5 py-1 text-xs font-bold text-teal-800 dark:bg-teal-950/60 dark:text-teal-200">{isOwner ? 'Family Organizer' : 'Parent/Guardian'}</span>
                </div>
              )}
              <p className="mt-1 break-all text-sm text-gray-600 dark:text-slate-300">{user?.email || parent?.email || '—'}</p>
            </div>
            <Button variant="secondary" onClick={signOut} className="shrink-0">Sign Out</Button>
          </div>
        </Card>

        <FamilyBillingCard />

        <PushSettings />

        <Card className="p-4 sm:p-5">
          <div className="mb-3 flex items-center gap-2">
            <h2 className="text-lg font-extrabold">Email Digests</h2>
            <InfoTooltip compact label="About email digests">These preferences apply only to your email. Family account and billing notices are still sent when needed. Digests include each child’s first name and activity totals; open MyPiggy for the full report.</InfoTooltip>
          </div>
          <p className="mb-3 text-sm text-gray-600 dark:text-slate-300">Choose the family activity summaries you receive.</p>
          {digestLoading ? <p role="status" className="text-sm">Loading email preferences...</p> : (
            <>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { key: 'weekly', title: 'Weekly Summary', schedule: 'Mondays · 9 a.m.' },
                  { key: 'monthly', title: 'Monthly Summary', schedule: 'First day of the month · 9 a.m.' },
                ].map((option) => (
                  <label key={option.key} className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3 transition focus-within:ring-2 focus-within:ring-teal-500 sm:p-4 ${digestDraft[option.key] ? 'border-teal-300 bg-teal-50 dark:border-teal-700 dark:bg-teal-950/35' : 'border-gray-200 bg-gray-50 dark:border-slate-600 dark:bg-slate-900/50'}`}>
                    <input type="checkbox" checked={digestDraft[option.key]} disabled={digestSaving || digestLoadFailed}
                      aria-label={option.title} onChange={(event) => setDigestDraft((current) => ({ ...current, [option.key]: event.target.checked }))} className="mt-1 h-4 w-4 shrink-0 accent-teal-600" />
                    <span className="min-w-0"><span className="block text-sm font-extrabold">{option.title}</span><span className="mt-1 block text-xs text-gray-600 dark:text-slate-300">{option.schedule}</span></span>
                  </label>
                ))}
              </div>
              <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
                <p className="text-xs text-gray-600 dark:text-slate-300">Delivery times follow your family time zone.</p>
                <Button variant="primary" onClick={saveDigests} disabled={digestLoadFailed || digestSaving || (digest.weekly === digestDraft.weekly && digest.monthly === digestDraft.monthly)}>{digestSaving ? 'Saving...' : 'Save Email Preferences'}</Button>
              </div>
            </>
          )}
        </Card>

        <div className="grid items-stretch gap-4 lg:grid-cols-2">
        <Card className="flex flex-col gap-3 p-4 sm:p-5">

          <div className="flex flex-col gap-3">

            <div className="">

              <h2 className="text-lg font-extrabold text-gray-900 dark:text-slate-100 mb-1">
                Appearance
              </h2>


              <p className="text-sm text-gray-600 dark:text-slate-300">
                Choose a look for parent pages. Your saved theme follows you across devices.
              </p>

            </div>


            <div
              className="inline-flex self-start rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-900 p-1"
              role="group"
              aria-label="Parent theme"
            >

              <button
                type="button"
                onClick={() =>
                  setThemeDraft(
                    PARENT_THEMES.LIGHT
                  )
                }
                disabled={
                  savingTheme ||
                  isWriteLocked
                }
                aria-pressed={
                  themeDraft ===
                    PARENT_THEMES.LIGHT
                }
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  themeDraft ===
                  PARENT_THEMES.LIGHT
                    ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200'
                    : 'text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white'
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                ☀️ Light
              </button>


              <button
                type="button"
                onClick={() =>
                  setThemeDraft(
                    PARENT_THEMES.DARK
                  )
                }
                disabled={
                  savingTheme ||
                  isWriteLocked
                }
                aria-pressed={
                  themeDraft ===
                    PARENT_THEMES.DARK
                }
                className={`rounded-lg px-4 py-2 text-sm font-semibold transition ${
                  themeDraft ===
                  PARENT_THEMES.DARK
                    ? 'bg-slate-700 text-white shadow-sm ring-1 ring-slate-600'
                    : 'text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-white'
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                🌙 Dark
              </button>

            </div>

          </div>

          <div className="mt-auto flex justify-end pt-3">
            <Button
              variant="primary"
              onClick={handleThemeSave}
              disabled={
                isWriteLocked ||
                savingTheme ||
                themeDraft === theme
              }
            >
              {savingTheme
                ? 'Saving Theme...'
                : 'Save Theme'}
            </Button>
          </div>


          <div className="mt-1 min-h-5 text-xs text-gray-500 dark:text-slate-300">
            {savingTheme
              ? 'Saving theme preference...'
              : (
                  themeError ||
                  `Current theme: ${
                    theme ===
                    PARENT_THEMES.DARK
                      ? 'Dark'
                      : 'Light'
                  }`
                )}
          </div>

        </Card>



        <Card className="flex flex-col gap-3 p-4 sm:p-5">

          <div className="flex flex-col gap-3">
            <div className="">
              <h2 className="text-lg font-extrabold text-gray-900 dark:text-slate-100 mb-1">
                Family Time Zone
              </h2>
              <p className="text-sm text-gray-600 dark:text-slate-300">
                Daily chores reset using this shared time zone.
              </p>
            </div>

            {loadingFamilyTimeZone ? (
              <p className="text-sm text-gray-500 dark:text-slate-300">
                Loading time zone...
              </p>
            ) : isOwner ? (
              <div className="w-full">
                <label
                  htmlFor="settings-family-time-zone"
                  className="sr-only"
                >
                  Family Time Zone
                </label>
                <select
                  id="settings-family-time-zone"
                  value={familyTimeZoneDraft}
                  onChange={(event) =>
                    setFamilyTimeZoneDraft(event.target.value)
                  }
                  disabled={
                    isWriteLocked ||
                    savingFamilyTimeZone
                  }
                  className="w-full rounded-lg border border-gray-300 dark:border-slate-600 bg-white dark:bg-slate-900 px-3 py-2.5 text-gray-900 dark:text-slate-100 disabled:opacity-60"
                >
                  {getFamilyTimeZoneOptions(familyTimeZoneDraft).map(
                    (option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    )
                  )}
                </select>
              </div>
            ) : (
              <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800/60 px-4 py-2.5 font-medium text-gray-900 dark:text-slate-100">
                {getFamilyTimeZoneLabel(familyTimeZone)}
              </div>
            )}
          </div>

          <div className="mt-auto pt-3 flex flex-wrap items-center justify-between gap-3">
            <p className="text-xs text-gray-500 dark:text-slate-300">
              {isOwner
                ? 'Organizer-only setting.'
                : 'Ask the Family Organizer if this time zone needs to be changed.'}
            </p>

            {isOwner && (
              <Button
                variant="primary"
                onClick={handleFamilyTimeZoneSave}
                disabled={
                  isWriteLocked ||
                  loadingFamilyTimeZone ||
                  savingFamilyTimeZone ||
                  familyTimeZoneDraft === familyTimeZone
                }
              >
                {savingFamilyTimeZone
                  ? 'Saving Time Zone...'
                  : 'Save Time Zone'}
              </Button>
            )}
          </div>

        </Card>



        </div>

        <section id="mypiggy-apps" ref={appsSection} tabIndex={-1} aria-label="MyPiggy apps" className="grid scroll-mt-24 gap-4 rounded-3xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500 lg:grid-cols-2">
          <AppDownloadCard audience="parents" />
          <AppDownloadCard audience="kids" />
        </section>

        <Card className="p-4 sm:p-5">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h2 className="text-lg font-extrabold">Help &amp; Guidance</h2>
              <p className="mt-1 text-sm text-gray-600 dark:text-slate-300">Explore dashboard shortcuts, child cards, and notifications again.</p>
            </div>
            <Button type="button" variant="secondary" onClick={() => navigate('/parent', { state: { dashboardTour: 'replay' } })}>Replay Dashboard Tour</Button>
          </div>
        </Card>
      </div>
    </AppLayout>
  );
}

